import type { QueryClient } from "@tanstack/react-query";

import {
  authSession,
  AUTH_STORAGE_KEY,
  parseStoredSession,
} from "@/features/auth";
import { configureApiAuth } from "@/services/http/api-client";

/** App-owned bridge between public auth state, the QueryClient, and browser lifecycle events. */
export function connectSessionLifecycle(
  queryClient: QueryClient,
  browserWindow: Window | undefined = typeof window === "undefined"
    ? undefined
    : window,
) {
  let expiryTimer: ReturnType<typeof setTimeout> | undefined;

  const scheduleExpiry = () => {
    clearTimeout(expiryTimer);
    const session = authSession.getSnapshot().session;
    if (!session) return;
    // Browser timers cap at a signed 32-bit delay; long token lifetimes need re-arming.
    expiryTimer = setTimeout(
      () => {
        authSession.expireIfNeeded();
        scheduleExpiry();
      },
      Math.min(Math.max(session.expiresAt - Date.now(), 0), 2_147_483_647),
    );
  };

  const unsubscribe = authSession.subscribe((state, previous) => {
    if (state.generation === previous.generation) return;
    // Query removal cancels reads before React renders the replacement scope.
    // Current query/mutation caches are private, including login variables and signed join URLs.
    queryClient.clear();
    scheduleExpiry();
  });
  const disconnectTransport = configureApiAuth(
    () => {
      authSession.expireIfNeeded();
      const state = authSession.getSnapshot();
      return {
        accessToken: state.session?.accessToken ?? null,
        scope: state.requestScope,
        generation: state.generation,
      };
    },
    (generation) => {
      if (authSession.getSnapshot().generation === generation)
        authSession.clearSession();
    },
  );

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== AUTH_STORAGE_KEY && event.key !== null) return;
    try {
      if (!browserWindow || event.storageArea !== browserWindow.localStorage)
        return;
      // Read the latest value rather than replaying a possibly stale queued event.
      const session = parseStoredSession(
        browserWindow.localStorage.getItem(AUTH_STORAGE_KEY),
      );
      if (
        JSON.stringify(session) !==
        JSON.stringify(authSession.getSnapshot().session)
      ) {
        authSession.restoreSession(session);
      }
    } catch {
      // Restricted storage must not terminate an otherwise valid in-memory session.
    }
  };
  const handleResume = () => authSession.expireIfNeeded();
  browserWindow?.addEventListener("storage", handleStorage);
  browserWindow?.addEventListener("focus", handleResume);
  browserWindow?.document.addEventListener("visibilitychange", handleResume);
  authSession.expireIfNeeded();
  scheduleExpiry();

  return () => {
    unsubscribe();
    disconnectTransport();
    clearTimeout(expiryTimer);
    browserWindow?.removeEventListener("storage", handleStorage);
    browserWindow?.removeEventListener("focus", handleResume);
    browserWindow?.document.removeEventListener(
      "visibilitychange",
      handleResume,
    );
  };
}
